/**
 * FeatureFlagService - Angular service for managing feature flags
 * Uses UnleashClient directly from unleash-proxy-client package
 * Based on implementation from frontend-utilities repository
 */

import { inject, Injectable } from '@angular/core';
import { distinctUntilChanged, map, Observable, of, ReplaySubject, startWith } from 'rxjs';
import { EVENTS, IConfig, IMutableContext, UnleashClient } from 'unleash-proxy-client';
import { FEATURE_FLAGS_CONFIG, FeatureFlagsConfig } from './feature-flags.types';

/**
 * Service for managing feature flags using the Unleash Proxy Client.
 * Provides observables and methods to check feature flag states and variants,
 * and to update the feature flag context.
 */
@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    readonly configs = inject<FeatureFlagsConfig>(FEATURE_FLAGS_CONFIG);

    /**
     * Emits after the SDK has read local cached data in the storageProvider.
     */
    readonly initialized$: Observable<void> = of();

    /**
     * Emits after the SDK has successfully started and performed the initial fetch of flags via the network.
     */
    readonly ready$: Observable<void> = of();

    /**
     * Emits every time Unleash returns a new feature toggle configuration.
     */
    readonly update$: Observable<void> = of();

    /**
     * Emits every time an error occurs when fetching feature flags.
     */
    readonly error$: Observable<Error> = of();

    private unleashClient: UnleashClient | undefined;

    constructor() {
        if (!this.configs.enabled) {
            return;
        }

        const unleashConfig: IConfig = {
            ...this.configs,
            url: this.configs.url ?? 'https://api.bannerflow.com/features/api/frontend/'
        };
        this.unleashClient = new UnleashClient(unleashConfig);

        const initializedSubject = new ReplaySubject<void>(1);
        const readySubject = new ReplaySubject<void>(1);
        const updateSubject = new ReplaySubject<void>(1);
        const errorSubject = new ReplaySubject<Error>(1);

        this.unleashClient.once(EVENTS.INIT, () => {
            initializedSubject.next();
            initializedSubject.complete();
        });
        this.unleashClient.on(EVENTS.READY, () => {
            readySubject.next();
            readySubject.complete();
        });
        this.unleashClient.on(EVENTS.UPDATE, () => {
            updateSubject.next();
        });
        this.unleashClient.on(EVENTS.ERROR, (error: Error) => {
            errorSubject.next(error);
        });

        this.initialized$ = initializedSubject.asObservable();
        this.ready$ = readySubject.asObservable();
        this.update$ = updateSubject.asObservable();
        this.error$ = errorSubject.asObservable();
    }

    /**
     * Starts the Unleash client if feature flags are enabled.
     * Note: always register event listeners before you call unleash.start()
     */
    start(): void {
        if (!this.configs.enabled) {
            return;
        }
        this.unleashClient?.start();
    }

    /**
     * Stops the Unleash client if feature flags are enabled.
     * Once the client has been stopped, it will no longer check for updates or send metrics to the server.
     */
    stop(): void {
        if (!this.configs.enabled) {
            return;
        }
        this.unleashClient?.stop();
    }

    /**
     * Sets a single context field for the Unleash client.
     * @param field The context field name
     * @param value The value to set for the context field
     */
    setContextField(field: string, value: string): void {
        if (!this.configs.enabled) {
            return;
        }
        this.unleashClient?.setContextField(field, value);
    }

    /**
     * Updates the Unleash client context with the provided context object.
     * @param context The new context
     */
    async updateContext(context: IMutableContext): Promise<void> {
        if (!this.configs.enabled) {
            return;
        }
        return this.unleashClient?.updateContext(context);
    }

    /**
     * Remove a single context field from the Unleash context.
     * @param field The context field name to remove from the context
     */
    removeContextField(field: string): Promise<void> | undefined {
        if (!this.configs.enabled) {
            return;
        }
        return this.unleashClient?.removeContextField(field);
    }

    /**
     * Checks if a feature flag is enabled.
     * @param featureFlag The name of the feature flag
     * @param defaultValue The default value to return if feature flags are disabled or the flag is not found
     */
    isEnabled(featureFlag: string, defaultValue = false): boolean {
        if (!this.configs.enabled) {
            return defaultValue;
        }
        return this.unleashClient?.isEnabled(featureFlag) ?? defaultValue;
    }

    /**
     * Checks if a feature flag is disabled.
     * @param featureFlag The name of the feature flag
     */
    isDisabled(featureFlag: string): boolean {
        return !this.isEnabled(featureFlag);
    }

    /**
     * Returns an observable that emits the enabled state of a feature flag.
     * @param featureFlag The name of the feature flag
     * @param defaultValue The default value to emit if feature flags are disabled or the flag is not found
     */
    isEnabled$(featureFlag: string, defaultValue = false): Observable<boolean> {
        return this.update$.pipe(
            startWith(defaultValue),
            map(() => this.isEnabled(featureFlag, defaultValue)),
            distinctUntilChanged()
        );
    }

    /**
     * Returns an observable that emits the disabled state of a feature flag.
     * @param featureFlag The name of the feature flag
     */
    isDisabled$(featureFlag: string): Observable<boolean> {
        return this.isEnabled$(featureFlag).pipe(map(flagState => !flagState));
    }

    /**
     * Gets the variant value for a feature flag.
     * @param featureFlag The name of the feature flag
     * @param defaultValue The default value to return if feature flags are disabled or the variant is not found
     */
    getVariant(featureFlag: string, defaultValue = ''): string {
        if (!this.configs.enabled) {
            return defaultValue;
        }
        const variant = this.unleashClient?.getVariant(featureFlag);

        if (!variant || !variant.enabled || !variant.payload) {
            return defaultValue;
        }
        return variant.payload.value;
    }

    /**
     * Returns an observable that emits the variant value of a feature flag.
     * @param featureFlag The name of the feature flag
     * @param defaultValue The default value to emit if feature flags are disabled or the variant is not found
     */
    getVariant$(featureFlag: string, defaultValue = ''): Observable<string> {
        return this.update$.pipe(
            startWith(defaultValue),
            map(() => this.getVariant(featureFlag, defaultValue)),
            distinctUntilChanged()
        );
    }
}
